import {
  Component,
  computed,
  effect,
  inject,
  signal,
  viewChild,
  DestroyRef,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {
  O2SheetImports,
  O2SheetComponent,
  HlmButtonImports,
  O2AccordionImports,
  HlmLabelImports,
  HlmInput,
  HlmFormFieldImports,
  O2PasswordInputComponent,
  HlmSwitchImports,
} from "@o2-fe/ui";
import {
  LoggedUserService,
  matchFieldValidator,
  differentFromFieldValidator,
  ThemeService,
} from "@o2-fe/core";
import { toSignal, takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AuthService } from "@o2-fe/auth";
import { ToastService } from "@o2-fe/toast-message";
import { forkJoin, Observable } from "rxjs";
import { catchError } from "rxjs/operators";
import { ProfileFormValues } from "../../interfaces/user-settings-form.interface";

@Component({
  selector: "app-user-settings-sheet",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    O2SheetImports,
    HlmButtonImports,
    O2AccordionImports,
    HlmLabelImports,
    HlmInput,
    HlmFormFieldImports,
    O2PasswordInputComponent,
    HlmSwitchImports,
  ],
  templateUrl: "./user-settings-sheet.component.html",
})
export class UserSettingsSheetComponent {
  private readonly _loggedUserService = inject(LoggedUserService);
  private readonly _authService = inject(AuthService);
  private readonly _toastService = inject(ToastService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _themeService = inject(ThemeService);

  // ViewChild for the sheet
  protected readonly sheetRef = viewChild.required(O2SheetComponent);

  // Get current user
  protected readonly user = toSignal(this._loggedUserService.user$);

  // Theme preference from service
  protected readonly themePreference = this._themeService.themePreference;

  // Computed: is custom mode (false = system, true = custom)
  protected readonly isCustomTheme = computed(
    () => this.themePreference() !== "system",
  );

  // Computed: is dark mode (when custom)
  protected readonly isDarkMode = computed(
    () => this.themePreference() === "dark",
  );

  // Compute sheet title
  protected readonly sheetTitle = computed(() => {
    const currentUser = this.user();
    if (!currentUser?.name || !currentUser?.surname)
      return "Impostazioni Utente";
    return `Profilo ${currentUser.name} ${currentUser.surname}`;
  });

  // Profile form (email is readonly, other fields are editable)
  protected readonly profileForm = new FormGroup({
    name: new FormControl<string>("", [
      Validators.required,
      Validators.minLength(2),
    ]),
    surname: new FormControl<string>("", [
      Validators.required,
      Validators.minLength(2),
    ]),
    phone: new FormControl<string>("", [
      Validators.pattern(/^[+]?[\d\s\-()]+$/),
      Validators.minLength(8),
      Validators.maxLength(20),
    ]),
    email: new FormControl<string>({ value: "", disabled: true }),
  });

  // Password form with strong password validation
  protected readonly passwordForm = new FormGroup({
    oldPassword: new FormControl<string>("", {
      nonNullable: true,
      validators: [Validators.required],
    }),
    newPassword: new FormControl<string>("", {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        ),
        differentFromFieldValidator("oldPassword", "sameAsOldPassword"),
      ],
    }),
    confirmPassword: new FormControl<string>("", {
      nonNullable: true,
      validators: [
        Validators.required,
        matchFieldValidator("newPassword", "passwordMismatch"),
      ],
    }),
  });

  protected readonly errorMessage = signal<string | undefined>(undefined);
  protected readonly hasError = computed(() => Boolean(this.errorMessage()));

  // Track initial form values for change detection
  private readonly _initialProfileValues = signal<ProfileFormValues | null>(
    null,
  );

  // Convert form valueChanges to signals for reactivity
  private readonly _profileFormValue = toSignal(this.profileForm.valueChanges, {
    initialValue: this.profileForm.getRawValue(),
  });

  private readonly _passwordFormValue = toSignal(
    this.passwordForm.valueChanges,
    {
      initialValue: this.passwordForm.getRawValue(),
    },
  );

  // Track password form status changes
  private readonly _passwordFormStatus = toSignal(
    this.passwordForm.statusChanges,
    {
      initialValue: this.passwordForm.status,
    },
  );

  // Computed signal to check if profile form has changes
  protected readonly hasProfileChanges = computed(() => {
    const initial = this._initialProfileValues();
    if (!initial) return false;

    // Trigger reactivity by accessing the signal
    const current = this._profileFormValue();

    // Normalize empty strings to compare properly
    const normalizeName = (current.name || "").trim();
    const normalizeSurname = (current.surname || "").trim();
    const normalizePhone = (current.phone || "").trim();
    const normalizeInitialName = (initial.name || "").trim();
    const normalizeInitialSurname = (initial.surname || "").trim();
    const normalizeInitialPhone = (initial.phone || "").trim();

    return (
      normalizeName !== normalizeInitialName ||
      normalizeSurname !== normalizeInitialSurname ||
      normalizePhone !== normalizeInitialPhone
    );
  });

  // Computed signal to check if password form has valid changes
  protected readonly hasPasswordChanges = computed(() => {
    // Access signals to trigger reactivity
    this._passwordFormValue();
    this._passwordFormStatus();

    // Check if form is dirty (user has interacted) and valid
    return this.passwordForm.dirty && this.passwordForm.valid;
  });

  // Computed signal to check if save button should be disabled
  protected readonly isSaveDisabled = computed(() => {
    const hasChanges = this.hasProfileChanges() || this.hasPasswordChanges();

    // If no changes, disable save button
    if (!hasChanges) {
      return true;
    }

    // If profile has changes, check if it's valid
    if (this.hasProfileChanges() && this.profileForm.invalid) {
      return true;
    }

    // If password has changes, check if it's valid
    if (this.hasPasswordChanges() && this.passwordForm.invalid) {
      return true;
    }

    // All validations passed
    return false;
  });

  constructor() {
    // Revalidate confirmPassword when newPassword changes
    this.passwordForm.controls.newPassword.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.passwordForm.controls.confirmPassword.updateValueAndValidity();
      });

    // Revalidate newPassword when oldPassword changes
    this.passwordForm.controls.oldPassword.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.passwordForm.controls.newPassword.updateValueAndValidity();
      });

    // Effect to cleanup forms when sheet closes (any way it closes)
    effect(() => {
      const isOpen = this.sheetRef().isOpen();

      // When sheet closes, reset forms and errors
      if (!isOpen) {
        this.profileForm.reset();
        this.passwordForm.reset();
        this.errorMessage.set(undefined);
        this._initialProfileValues.set(null);
      }
    });
  }

  /**
   * Opens the sheet and loads user data
   */
  open(): void {
    const currentUser = this.user();
    if (currentUser) {
      const profileValues: ProfileFormValues = {
        name: currentUser.name || "",
        surname: currentUser.surname || "",
        phone: currentUser.phone || "",
        email: currentUser.email || "",
      };

      // Force valueChanges emission to sync the signal
      this.profileForm.patchValue(profileValues, { emitEvent: true });

      // Store initial values for change detection
      this._initialProfileValues.set(profileValues);
    }
    this.sheetRef().open();
  }

  /**
   * Closes the sheet
   * Cleanup is handled automatically by the effect watching isOpen signal
   */
  close(): void {
    this.sheetRef().close();
  }

  /**
   * Handle theme mode switch (system vs custom)
   * @param isCustom - True if user wants custom theme, false for system
   */
  protected onThemeModeChange(isCustom: boolean): void {
    if (isCustom) {
      // Switch to custom mode - default to light
      this._themeService.setThemePreference("light");
    } else {
      // Switch to system mode
      this._themeService.setThemePreference("system");
    }
  }

  /**
   * Handle dark mode switch (only when custom mode is active)
   * @param isDark - True for dark mode, false for light mode
   */
  protected onDarkModeChange(isDark: boolean): void {
    // Only apply if in custom mode
    if (this.isCustomTheme()) {
      this._themeService.setThemePreference(isDark ? "dark" : "light");
    }
  }

  /**
   * Save changes (called from footer)
   * Handles both profile update and password change intelligently
   */
  protected save(): void {
    const { newPassword, oldPassword } = this.passwordForm.getRawValue();
    const hasPasswordChange = Boolean(newPassword);

    // Validate password if provided
    if (hasPasswordChange && this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    // Validate profile form if it has changes
    if (this.hasProfileChanges() && this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    this.errorMessage.set(undefined);

    // Build operations array dynamically
    const operations: { key: string; operation: Observable<unknown> }[] = [];

    // Add profile update operation if needed
    if (this.hasProfileChanges()) {
      const profileValues = this.profileForm.getRawValue();
      const updates = {
        name: profileValues.name || undefined,
        surname: profileValues.surname || undefined,
        phone: profileValues.phone || undefined,
      };
      operations.push({
        key: "profile",
        operation: this._loggedUserService.updateProfile(updates),
      });
    }

    // Add password change operation if needed
    if (hasPasswordChange) {
      operations.push({
        key: "password",
        operation: this._authService.changePassword(oldPassword, newPassword),
      });
    }

    // Execute all operations in parallel
    if (operations.length === 0) {
      return;
    }

    const operationsMap = operations.reduce(
      (acc, { key, operation }) => {
        acc[key] = operation.pipe(
          catchError((error) => {
            throw error;
          }),
        );
        return acc;
      },
      {} as Record<string, Observable<unknown>>,
    );

    forkJoin(operationsMap)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          // Show success toast for password change (profile update toast is handled by interceptor)
          if (hasPasswordChange) {
            this._toastService.success("Password modificata con successo");
          }
          // Simply close the sheet, cleanup is handled by the effect
          this.close();
        },
        error: (error) => {
          // Determine which operation failed and show appropriate message
          const isPasswordError =
            error.message?.includes("password") ||
            error.code?.includes("Password");

          if (isPasswordError) {
            this.errorMessage.set(
              "Errore durante il cambio password. Verifica la password attuale.",
            );
            this._toastService.error(
              "Errore durante il cambio password. Verifica la password attuale.",
            );
          } else {
            this.errorMessage.set("Errore durante il salvataggio. Riprova.");
            this._toastService.error("Errore durante il salvataggio. Riprova.");
          }
        },
      });
  }
}
